// Compatibility layer so the react-router v5 API surface this app was built
// against (useHistory, withRouter, Switch, Redirect) keeps working on top of
// react-router-dom v7's hooks-only API, without touching call sites.
import {
  Navigate,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

export * from "react-router-dom";

// v5's <Switch> == v6+'s <Routes> (ranked matching; no overlapping paths here).
export const Switch = Routes;

// v5's <Redirect to="..." />.
export function Redirect({ to, ...rest }) {
  return <Navigate to={to} replace {...rest} />;
}

// v5's useHistory(): an object shaped like the old `history` instance,
// backed by v6+'s useNavigate/useLocation. Only .push/.replace/.location
// are used anywhere in this app.
export function useHistory() {
  const navigate = useNavigate();
  const location = useLocation();
  return {
    push: (to, state) => navigate(to, { state }),
    replace: (to, state) => navigate(to, { replace: true, state }),
    goBack: () => navigate(-1),
    go: (delta) => navigate(delta),
    location,
  };
}

// v5's withRouter(Component): injects {history, location, match} props.
export function withRouter(Component) {
  function WithRouterShim(props) {
    const history = useHistory();
    const params = useParams();
    return (
      <Component
        {...props}
        history={history}
        location={history.location}
        match={{ params }}
      />
    );
  }
  WithRouterShim.displayName = `withRouter(${
    Component.displayName || Component.name || "Component"
  })`;
  return WithRouterShim;
}
